import { useState } from "react";
import { Badge, Filter, InputText, type FilterItem, SectionCard } from "@/deps/ui-library";
import { useAuditTrail } from "@/api/queries";
import type { AuditEntry } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { formatDateTime } from "@/lib/dates";

/**
 * What happened, who did it, and when.
 *
 * It was five entries in a `const`. An audit trail with nothing behind it is worse than none: it
 * reads as evidence, and the one thing an auditor does with this screen is take what it says as
 * true. Every row now comes from the same fixtures the documents do.
 */
const KINDS: Array<AuditEntry["kind"] | "all"> = [
  "all",
  "created",
  "updated",
  "approved",
  "posted",
  "locked",
  "settings",
  "login",
];

const KIND_ITEMS: FilterItem[] = KINDS.map((option) => ({
  value: option,
  label: option === "all" ? "Everything" : option,
}));

const TONE: Record<AuditEntry["kind"], "default" | "info" | "warning" | "success"> = {
  created: "default",
  updated: "default",
  approved: "info",
  posted: "success",
  locked: "warning",
  settings: "warning",
  login: "default",
};

export function AuditTab() {
  // The search box is labelled with `aria-label` rather than a visible label: the card's title
  // already says what this is, and a second "Search" above the box is noise a screen reader hears
  // twice. The kind filter carries its name inside the chip, beside the value it holds.
  const [text, setText] = useState("");
  const [kind, setKind] = useState<AuditEntry["kind"] | "all">("all");

  const { data: entries, isPending } = useAuditTrail({
    q: text.trim() === "" ? undefined : text.trim(),
    kind,
  });

  return (
    <SectionCard
      title="Audit trail"
      actions={
        <span className="flex items-center gap-2">
          <span className="w-56">
            <InputText.FilterSearch
              aria-label="Search the audit trail"
              value={text}
              placeholder="Document, person or detail"
              onChange={(event) => setText(event.target.value)}
              onClear={() => setText("")}
            />
          </span>
          <Filter.SingleSelect
            label="Kind of event"
            icon={false}
            searchable={false}
            clearable={false}
            items={KIND_ITEMS}
            value={KIND_ITEMS.find((item) => item.value === kind) ?? null}
            onValueChange={(item) =>
              setKind(item ? (String(item.value) as AuditEntry["kind"] | "all") : "all")
            }
          />
        </span>
      }
      flush
    >
      {isPending ? (
        <p className="text-fg-muted p-4 text-sm">Loading</p>
      ) : (entries?.length ?? 0) === 0 ? (
        <EmptyState
          icon="ScrollText"
          title="Nothing matches"
          description="Every issue, approval, posting and period close appears here. Narrow the search or choose a different kind."
        />
      ) : (
        <ul className="flex flex-col">
          {entries!.map((entry) => (
            <li
              key={entry.id}
              className="border-line-soft flex items-baseline gap-3 border-b px-4 py-2.5 last:border-0"
            >
              <span className="text-fg-hint num w-40 shrink-0 text-xs">
                {formatDateTime(entry.at)}
              </span>
              <Badge colorScheme={TONE[entry.kind]} size="sm">
                {entry.kind}
              </Badge>
              <span className="min-w-0 flex-1">
                <span className="text-fg block text-sm">
                  {entry.document ? (
                    <span className="num text-fg-muted mr-2">{entry.document}</span>
                  ) : null}
                  {entry.detail}
                </span>
              </span>
              <span className="text-fg-muted shrink-0 text-xs">{entry.user}</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
